import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { env } from '@/env';
import { chatBotManager } from '@/services/chatBotManager';
import { useAnalytics } from '@/hooks/useAnalytics';
import { VIEW_SUPPORT, CALL_SUPPORT, SUPPORT_CLOSE, CHATBOT } from '@/consts/events';

type Props = {
  open: boolean;
  onClose: () => void;
};

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
    <path
      opacity=".35"
      d="M19,3H5C3.343,3,2,4.343,2,6v13v0.828c0,1.746,1.974,2.762,3.395,1.747l2.822-2.016C8.726,19.195,9.336,19,9.961,19H19c1.657,0,3-1.343,3-3V6C22,4.343,20.657,3,19,3z"
    />
    <path d="M15,10H9c-0.553,0-1-0.448-1-1s0.447-1,1-1h6c0.553,0,1,0.448,1,1S15.553,10,15,10z" />
    <path d="M13,14H9c-0.553,0-1-0.448-1-1s0.447-1,1-1h4c0.553,0,1,0.448,1,1S13.553,14,13,14z" />
  </svg>
);

const CallIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
    <path
      opacity=".35"
      d="M16.48,3.086c-1.653,0.222-5.452,1.091-8.878,4.517s-4.295,7.224-4.517,8.878c-0.259,1.198,0.07,2.497,1.001,3.428c1.456,1.456,6.73-3.818,5.274-5.274l-0.929-0.929l5.274-5.274l0.929,0.929c1.456,1.456,6.73-3.818,5.274-5.274C18.977,3.155,17.678,2.826,16.48,3.086z"
    />
    <path d="M19.911,4.089l-5.273,5.273c1.457,1.453,3.815,1.453,5.271-0.003C21.363,7.905,21.364,5.546,19.911,4.089z" />
    <path d="M9.363,14.637L4.09,19.91c1.456,1.453,3.815,1.453,5.271-0.003C10.815,18.452,10.816,16.094,9.363,14.637z" />
  </svg>
);

const WregHelp = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  useEffect(() => {
    if (open) track({ event: VIEW_SUPPORT });
  }, [open, track]);

  const close = () => {
    track({ event: SUPPORT_CLOSE });
    onClose();
  };

  const openChat = () => {
    track({ event: CHATBOT });
    chatBotManager.open();
    onClose();
  };

  const call = () => {
    track({ event: CALL_SUPPORT });
    window.location.href = `tel:${env.SUPPORT_NUMBER}`;
  };

  const rows = [
    {
      id: 'chat',
      icon: <ChatIcon />,
      label: t('wreg.help.chat'),
      sub: t('wreg.help.chat_sub'),
      onClick: openChat,
    },
    {
      id: 'call',
      icon: <CallIcon />,
      label: t('wreg.help.call'),
      sub: t('wreg.help.call_sub', { number: env.SUPPORT_NUMBER }),
      tag: 'warning' as const,
      tagLabel: t('wreg.help.call_tag'),
      onClick: call,
    },
  ];

  return (
    <>
      <div className={`rg-help-back${open ? ' show' : ''}`} onClick={close} />
      <div
        className={`rg-help-sheet${open ? ' show' : ''}`}
        role="dialog"
        aria-modal={open}
        aria-hidden={!open}
        aria-label={t('wreg.help.title')}
        inert={open ? undefined : ''}
      >
        <div className="rg-help-grip" />
        <div className="rg-help-head">
          <button type="button" className="rg-help-close" onClick={close}>
            {t('wreg.help.close')}
          </button>
        </div>
        <div className="rg-help-title">{t('wreg.help.title')}</div>
        {rows.map((r) => (
          <button type="button" className="rg-help-row" key={r.id} onClick={r.onClick}>
            <span className="rg-help-row-main">
              <span className="rg-help-row-chip">{r.icon}</span>
              <span>
                <span className="rg-help-row-titlerow">
                  <span className="rg-help-row-label">{r.label}</span>
                  {r.tag && (
                    <span className={`rg-help-tag ${r.tag}`}>
                      <span className="dot" />
                      {r.tagLabel}
                    </span>
                  )}
                </span>
                <span className="rg-help-row-sub">{r.sub}</span>
              </span>
            </span>
            <span className="rg-help-chev" aria-hidden="true">›</span>
          </button>
        ))}
      </div>
    </>
  );
};

export { WregHelp };
